'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { fetchCars, fetchBrands } from '@/lib/supabase/dinauto-service';
import type { Car, CarCategory, Brand } from '@/lib/supabase/dinauto-service';
import CarCard from '@/components/CarCard';
import CarDetailModal from '@/components/CarDetailModal';
import { Filter, X } from 'lucide-react';

const CATEGORIES: CarCategory[] = ['Economy', 'Comfort', 'SUV'];

export default function CarsGalleryClient() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [cars, setCars] = useState<Car[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<CarCategory | ''>(
    (searchParams.get('category') as CarCategory) || ''
  );
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [carsData, brandsData] = await Promise.all([fetchCars(), fetchBrands()]);
      setCars(carsData);
      setBrands(brandsData);
      setLoading(false);
    }
    load();
  }, []);

  const brandNames = useMemo(() => brands.map((b) => b.name), [brands]);

  const filtered = useMemo(() => {
    return cars.filter((car) => {
      if (car.status === 'Hidden') return false;
      if (selectedCategory && car.category !== selectedCategory) return false;
      if (selectedBrand && car.brand !== selectedBrand) return false;
      return true;
    });
  }, [cars, selectedCategory, selectedBrand]);

  const counts = useMemo(() => ({
    total: cars.filter((c) => c.status !== 'Hidden').length,
    Economy: cars.filter((c) => c.category === 'Economy' && c.status !== 'Hidden').length,
    Comfort: cars.filter((c) => c.category === 'Comfort' && c.status !== 'Hidden').length,
    SUV: cars.filter((c) => c.category === 'SUV' && c.status !== 'Hidden').length,
  }), [cars]);

  const handleReserve = (car: Car) => {
    router.push(`/reservation?carId=${car.id}`);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
  };

  const hasFilters = selectedCategory || selectedBrand;

  if (loading) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 text-center">
        <div className="inline-block w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mb-4" />
        <p className="text-muted-foreground">Loading fleet...</p>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      {/* Filter bar */}
      <div className="bg-white border border-border p-5 mb-8">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter size={15} />
              <span className="font-600">Filter:</span>
            </div>

            {/* Category pills */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-1.5 text-sm font-700 border transition-colors ${
                  !selectedCategory
                    ? 'bg-foreground text-white border-foreground'
                    : 'border-border hover:border-foreground text-muted-foreground hover:text-foreground'
                }`}
              >
                All ({counts.total})
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  key={`filter-cat-${cat}`}
                  onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                  className={`px-4 py-1.5 text-sm font-700 border transition-colors ${
                    selectedCategory === cat
                      ? 'bg-foreground text-white border-foreground'
                      : 'border-border hover:border-foreground text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat} ({counts[cat]})
                </button>
              ))}
            </div>

            {/* Brand filter */}
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="input-field py-1.5 text-sm w-auto"
            >
              <option value="">All Brands</option>
              {brandNames.map((b) => (
                <option key={`brand-filter-${b}`} value={b}>{b}</option>
              ))}
            </select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={14} />Clear
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{filtered.length} cars</p>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-20 text-center">
          <p className="font-black text-xl mb-2" style={{ fontWeight: 900 }}>No cars match your filters</p>
          <p className="text-muted-foreground">Try adjusting the category or brand filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((car) => (
            <CarCard
              key={car.id}
              car={car}
              onViewDetail={() => setSelectedCar(car)}
              onReserve={() => handleReserve(car)}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedCar && (
        <CarDetailModal
          car={selectedCar}
          onClose={() => setSelectedCar(null)}
          onReserve={() => { setSelectedCar(null); handleReserve(selectedCar); }}
        />
      )}
    </div>
  );
}